import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Briefcase, LineChart, Scissors, Sparkles, Calculator,
  Network, ShieldCheck, TrendingUp, BellRing, Cpu, Zap, Trophy, Check,
  TrendingDown, BarChart3, Activity, DollarSign, Percent, Hash, ArrowUpRight, ArrowDownRight, Target,
} from "lucide-react";
import { Wordmark } from "@/components/BrandMark";
import { ThemeToggle } from "@/components/ThemeToggle";

function Football({ size = 16, className = "" }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" className={className}>
      <g transform="rotate(-35 12 12)">
        <ellipse cx="12" cy="12" rx="10" ry="5.2" stroke="currentColor" strokeWidth="1.7" />
        <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="1.5" />
        <line x1="10" y1="10.4" x2="10" y2="13.6" stroke="currentColor" strokeWidth="1.5" />
        <line x1="12" y1="10.2" x2="12" y2="13.8" stroke="currentColor" strokeWidth="1.5" />
        <line x1="14" y1="10.4" x2="14" y2="13.6" stroke="currentColor" strokeWidth="1.5" />
      </g>
    </svg>
  );
}

const RAIN_ICONS = [TrendingUp, TrendingDown, BarChart3, LineChart, Activity, DollarSign, Percent, Hash, ArrowUpRight, ArrowDownRight, Target, Trophy, Football];

function MatrixRain() {
  const cols = useMemo(
    () => Array.from({ length: 28 }, (_, i) => ({
      left: `${(i / 28) * 100}%`,
      dur: 13 + Math.random() * 14,
      delay: -Math.random() * 22,
      icons: Array.from({ length: 16 }, () => RAIN_ICONS[Math.floor(Math.random() * RAIN_ICONS.length)]),
    })),
    []
  );
  return (
    <div className="matrix-rain" aria-hidden>
      {cols.map((c, i) => (
        <div key={i} className="rain-col" style={{ left: c.left, animationDuration: `${c.dur}s`, animationDelay: `${c.delay}s` }}>
          {c.icons.map((Ic, j) => <Ic key={j} size={16} strokeWidth={1.6} />)}
        </div>
      ))}
    </div>
  );
}

const FEATURES = [
  { icon: LineChart, title: "Pick Futures Engine", tag: "FLAGSHIP",
    desc: "10,000-simulation Monte-Carlo engine projects every team's future draft slot off real weekly projections — then flags BUY / SELL on their picks before the market catches up.", span: "md:col-span-2 md:row-span-2", big: true },
  { icon: Briefcase, title: "Multi-League Portfolio", tag: null,
    desc: "Sync unlimited Sleeper accounts into one command center. Track player exposure %, position concentration, and surface cross-league buy-low & sell-high targets." },
  { icon: Scissors, title: "Roster Crunch Optimizer", tag: null,
    desc: "Drop Priority Scores surface forced-cut candidates, then auto-build 2-for-1 packages that salvage value before the deadline." },
  { icon: Sparkles, title: "AI Trade Advisor", tag: "CLAUDE",
    desc: "Claude-powered analysis + persona-based trade pitches written to land with each leaguemate's psychology." },
  { icon: Network, title: "DME League Intelligence", tag: null,
    desc: "Values re-priced for your league's exact settings — Dynamic Value-Over-Replacement, Manager Personas & a Desperation Index expose who's weak and ready to deal." },
  { icon: ShieldCheck, title: "Proprietary Value Engine", tag: "VERIFIED",
    desc: "Every player & pick is continuously re-priced by blending live Market Sentiment with Observed Transaction Data, then age-adjusted by position and run through an automated outlier-smoothing filter to produce a True Market Clearing Price — the S&P 500 Index of fantasy football." },
  { icon: Calculator, title: "Trade Calculator + Verdict Cards", tag: null,
    desc: "Balance players + picks across both sides and export a shareable Trade Verdict Card." },
  { icon: TrendingUp, title: "Power Rankings & Market Trends", tag: null,
    desc: "Live rankings mapped to your roster, plus 7/14/30-day risers & fallers with sparklines." },
  { icon: BellRing, title: "Real-Time Web Push Alerts", tag: null,
    desc: "Exposure spikes, market swings and roster risks pushed to your phone the moment they happen." },
];

const TICKER = [
  "10,000 SIMULATIONS RUN PER LEAGUE", "MARKET-VERIFIED PLAYER VALUES", "MULTI-ACCOUNT SLEEPER SYNC",
  "MONTE-CARLO DRAFT-SLOT PROJECTIONS", "AI TRADE PITCHES", "BUILT FOR DYNASTY CHAMPIONS",
];

export default function Landing() {
  const nav = useNavigate();
  const go = () => nav("/login?signup=1");
  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="border-b border-[var(--border)] sticky top-0 backdrop-blur-md bg-[var(--bg)]/80 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div data-testid="brand"><Wordmark size={32} sub="QUANTITATIVE DYNASTY" /></div>
          <div className="flex gap-2 items-center">
            <ThemeToggle />
            <button onClick={() => nav("/login")} className="btn-secondary text-sm" data-testid="login-link">Log in</button>
            <button onClick={go} className="btn-primary text-sm" data-testid="signup-link">Start Free</button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative terminal-grid overflow-hidden border-b border-[var(--border)]">
        <MatrixRain />
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-[var(--bg)]" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-36">
          <div className="max-w-3xl fade-up">
            <div className="inline-flex items-center gap-2 border border-[var(--accent)]/30 rounded-sm px-3 py-1 text-xs text-[var(--accent)] mb-6 font-mono" data-testid="hero-pill">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] animate-pulse" />
              MONTE-CARLO ENGINE · LIVE SLEEPER SYNC · 7-DAY FREE TRIAL
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter leading-[0.92] uppercase">
              Dynasty<br />with an <span className="text-[var(--accent)]" style={{ textShadow: "0 0 30px rgba(0,255,65,0.35)" }}>unfair edge.</span>
            </h1>
            <p className="text-[var(--text-2)] text-lg mt-6 max-w-2xl">
              Dynasty Metrics is your league's analytics command center. Simulate seasons 10,000 times,
              project every future pick, expose desperate managers, and let AI write the trade pitch —
              all from one matrix-green dashboard.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <button onClick={go} className="btn-primary inline-flex items-center gap-2" data-testid="hero-cta">
                Start Free Trial <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => nav("/pricing")} className="btn-secondary" data-testid="hero-pricing">
                See pricing
              </button>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              {[{ k: "10K", v: "season sims" }, { k: "$9.99", v: "per month" }, { k: "7d", v: "free trial" }].map(s => (
                <div key={s.k}>
                  <div className="font-mono text-3xl font-semibold text-white">{s.k}</div>
                  <div className="text-xs text-[var(--text-3)] uppercase tracking-wider">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Social proof ticker */}
      <div className="border-b border-[var(--border)] overflow-hidden py-3 bg-[var(--elev)]">
        <div className="ticker-track text-xs font-mono text-[var(--text-3)] tracking-[0.25em]">
          {[...TICKER, ...TICKER].map((t, i) => (
            <span key={i} className="mx-8 inline-flex items-center gap-8">{t}<span className="text-[var(--accent)]">•</span></span>
          ))}
        </div>
      </div>

      {/* Feature bento grid */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="mb-12">
          <div className="text-[var(--accent)] text-xs tracking-widest mb-2 font-mono">// THE FULL ARSENAL</div>
          <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">Every edge, one terminal.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 auto-rows-[1fr] gap-4">
          {FEATURES.map((f, i) => (
            <div key={i}
              className={`card-bordered p-6 hover:border-[var(--accent)]/50 hover:glow-accent transition-colors group relative overflow-hidden ${f.span || ""}`}
              data-testid={`feature-${i}`}>
              {f.big && <div className="absolute inset-0 terminal-grid opacity-40 pointer-events-none" />}
              <div className="relative flex items-start justify-between mb-4">
                <f.icon className={`text-[var(--accent)] ${f.big ? "w-9 h-9" : "w-6 h-6"} group-hover:scale-110 transition-transform`} strokeWidth={1.5} />
                {f.tag && <span className="text-[9px] font-mono tracking-widest px-2 py-0.5 border border-[var(--accent)]/30 text-[var(--accent)] rounded-sm">{f.tag}</span>}
              </div>
              <div className={`relative font-display font-bold mb-2 ${f.big ? "text-2xl md:text-3xl" : "text-xl"}`}>{f.title}</div>
              <div className={`relative text-[var(--text-2)] leading-relaxed ${f.big ? "text-base max-w-md" : "text-sm"}`}>{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: Network, n: "01", t: "Sync your leagues", d: "Connect one or many Sleeper accounts. We map every roster, pick and matchup." },
            { icon: Cpu, n: "02", t: "Run the simulations", d: "We project seasons 10,000 times and value every asset against the live market." },
            { icon: Trophy, n: "03", t: "Win the trades", d: "Act on BUY/SELL signals, fire off AI-written pitches, and corner your league." },
          ].map((s, i) => (
            <div key={i} className="card-bordered p-6" data-testid={`step-${i}`}>
              <div className="flex items-center justify-between mb-4">
                <s.icon className="w-6 h-6 text-[var(--accent)]" strokeWidth={1.5} />
                <span className="font-mono text-3xl text-[var(--text-3)]">{s.n}</span>
              </div>
              <div className="font-display text-xl font-bold mb-1">{s.t}</div>
              <div className="text-sm text-[var(--text-2)]">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Why DME reigns — proprietary value engine */}
      <section className="border-y border-[var(--border)] bg-black/40" data-testid="why-dme-section">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="mb-12 max-w-3xl">
            <div className="text-[var(--accent)] text-xs tracking-widest mb-2 font-mono">// THE DME ADVANTAGE</div>
            <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">
              Why <span className="text-[var(--accent)]" style={{ textShadow: "0 0 24px rgba(0,255,65,0.3)" }}>DME</span> values reign supreme.
            </h2>
            <p className="text-[var(--text-2)] mt-4 text-lg">
              Most sites hand you a single crowd-sourced number. The <span className="text-white font-semibold">Dynasty Metrics Evaluation (DME)</span> is
              engineered — blended from multiple live markets, bent to how dynasty assets truly age, and re-priced every day. It's the closest thing to a true market-clearing price in fantasy football.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Activity, t: "Dual-Market Blend", d: "We fuse live crowd Sentiment with Observed Real-Trade data into one weighted consensus — never trusting a single source the way other calculators do." },
              { icon: TrendingUp, t: "Dynasty Age Curves", d: "DME is bent to how assets actually age: the RB cliff, WR & TE speed decline, the QB longevity premium, and a youth/rookie breakout bonus — so it prices the future the market sees late." },
              { icon: ShieldCheck, t: "Outlier-Smoothed", d: "An automated filter tempers reactionary hype toward the true clearing price — the S&P 500 Index of fantasy football, not a popularity contest." },
              { icon: Cpu, t: "Self-Updating Daily", d: "Re-priced every day from live data with zero manual upkeep — rookies tag themselves in, declared prospects roll off. Your edge never goes stale." },
            ].map((c, i) => (
              <div key={i} className="card-bordered p-6 hover:border-[var(--accent)]/50 transition-colors" data-testid={`why-dme-${i}`}>
                <c.icon className="w-7 h-7 text-[var(--accent)] mb-4" strokeWidth={1.5} />
                <div className="font-display text-lg font-bold mb-2">{c.t}</div>
                <div className="text-sm text-[var(--text-2)] leading-relaxed">{c.d}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs font-mono text-[var(--text-3)] tracking-widest">
            <span className="inline-flex items-center gap-2"><Percent className="w-3.5 h-3.5 text-[var(--accent)]" /> AGE-ADJUSTED EVERY POSITION</span>
            <span className="inline-flex items-center gap-2"><BarChart3 className="w-3.5 h-3.5 text-[var(--accent)]" /> MULTI-MARKET CONSENSUS</span>
            <span className="inline-flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[var(--accent)]" /> HYPE-RESISTANT</span>
            <span className="inline-flex items-center gap-2"><Cpu className="w-3.5 h-3.5 text-[var(--accent)]" /> RE-PRICED DAILY</span>
          </div>
        </div>
      </section>

      {/* Free vs Pro */}
      <section className="max-w-5xl mx-auto px-6 pb-20" data-testid="free-tier-section">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-xs font-mono text-[var(--accent)] mb-3">
            <Check className="w-3.5 h-3.5" /> FREE FOREVER · NO CREDIT CARD
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold">Powerful even before you pay a cent</h2>
          <p className="text-[var(--text-2)] mt-3 max-w-2xl mx-auto">Create a free account and get real dynasty tools right away. Go Pro to win the league that matters — AI trade pitches, league-specific intelligence and draft-pick edges that pay for themselves in a single trade.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {/* Free card */}
          <div className="card-bordered p-7" data-testid="free-card">
            <div className="flex items-center justify-between mb-5">
              <div className="font-display text-xl font-bold">Free</div>
              <span className="font-mono text-xs tracking-widest text-[var(--accent)] border border-[var(--accent)]/40 bg-[var(--accent)]/10 rounded-sm px-2 py-1">$0 · NO CARD</span>
            </div>
            <ul className="space-y-3 text-sm">
              {[
                ["Trade Calculator + shareable Verdict Cards", "Settle any trade with proprietary DME values."],
                ["Live Power Rankings", "See where your roster stacks up, re-priced daily."],
                ["Market Trends", "7/14/30-day risers & fallers with sparklines."],
                ["DME player & pick values", "Our market-verified valuation on every asset."],
                ["Player profiles & value history", "Deep-dive any player's value trend."],
              ].map(([t, d]) => (
                <li key={t} className="flex gap-3">
                  <Check className="w-4 h-4 text-[var(--accent)] mt-0.5 shrink-0" />
                  <span><span className="text-white font-medium">{t}</span><span className="block text-[var(--text-3)] text-xs mt-0.5">{d}</span></span>
                </li>
              ))}
            </ul>
            <button onClick={go} className="btn-secondary w-full mt-7 inline-flex items-center justify-center gap-2" data-testid="free-cta">
              Create free account <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          {/* Pro card */}
          <div className="card-bordered neon-border p-7" data-testid="pro-card">
            <div className="flex items-center justify-between mb-5">
              <div className="font-display text-xl font-bold inline-flex items-center gap-2"><Zap className="w-4 h-4 text-[var(--accent)]" /> Pro</div>
              <span className="font-mono text-xs tracking-widest text-[var(--text-2)]">$9.99 / mo · 7-DAY TRIAL</span>
            </div>
            <div className="text-xs tracking-widest text-[var(--text-3)] font-mono mb-1">EVERYTHING IN FREE, PLUS</div>
            <div className="text-[11px] text-[var(--text-3)] mb-3">Less than a single waiver claim — most managers earn it back in one trade.</div>
            <ul className="space-y-2.5 text-sm">
              {[
                ["AI Trade Advisor & persona-based pitches", "Claude-drafted offers tailored to each rival manager's tendencies."],
                ["DME League Intelligence & Draft War Room", "Re-rank your league by your exact scoring, plus a live draft war room & trade heat map."],
                ["Pick Futures simulator & Roster Crunch", "10,000-sim Monte Carlo off real weekly projections grades your picks and roster holes."],
                ["Command Center & Sell-High Radar", "A daily action feed of the highest-leverage moves in your league."],
                ["Trade Desk: buy-low / sell-high finder", "Auto-scans every roster for buy-low windows and the best manager to sell to."],
                ["Rookie Draft board, Mock Draft & Pick Arbitrage", "Nail your rookie picks and find mispriced draft capital before anyone else."],
                ["Prospect Pipeline & Rookie Scouting reports", "Auto-updated devy board, scouting reports and a devy→pick value bridge."],
                ["League Ledger, Trade History & My-Squad news", "Log every deal and get news filtered to your exact roster, with push alerts."],
                ["Multi-League Portfolio & exposure alerts", "Running multiple leagues? Track exposure and risk across all of them at once."],
              ].map(([t, d]) => (
                <li key={t} className="flex gap-3">
                  <Zap className="w-4 h-4 text-[var(--accent)] mt-0.5 shrink-0" />
                  <span><span className="text-white font-medium">{t}</span><span className="block text-[var(--text-3)] text-xs mt-0.5">{d}</span></span>
                </li>
              ))}
            </ul>
            <button onClick={() => nav("/pricing")} className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2" data-testid="pro-card-cta">
              See Pro <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="max-w-3xl mx-auto px-6 pb-24">
        <div className="card-bordered neon-border p-10 md:p-14 text-center terminal-grid relative overflow-hidden" data-testid="pricing-block">
          <div className="inline-flex items-center gap-2 text-xs font-mono text-[var(--accent)] mb-4">
            <Zap className="w-3.5 h-3.5" /> DYNASTY METRICS PRO
          </div>
          <div className="flex items-end justify-center gap-2 mb-1">
            <span className="font-mono text-6xl font-semibold text-white">$9.99</span>
            <span className="text-[var(--text-2)] mb-2">/ month</span>
          </div>
          <div className="text-xs tracking-widest text-[var(--text-3)] mb-6 font-mono">7-DAY FREE TRIAL · CANCEL ANYTIME · NO CARD TO START</div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-[var(--text-2)] mb-8 max-w-lg mx-auto">
            {["Every feature unlocked", "Unlimited league sync", "10K-sim pick futures", "AI trade pitches"].map(p => (
              <span key={p} className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-[var(--accent)]" />{p}</span>
            ))}
          </div>
          <button onClick={go} className="btn-primary inline-flex items-center gap-2 text-base" data-testid="footer-cta">
            Start your free trial <ArrowRight className="w-4 h-4" />
          </button>
          <div className="text-xs text-[var(--text-3)] mt-5 font-mono">
            Got a <span className="text-[var(--accent)]">beta code</span>? Enter it at sign-up to unlock everything free — no card needed.
          </div>
        </div>
      </section>

      <footer className="border-t border-[var(--border)] py-8 text-center text-xs text-[var(--text-3)]">
        © {new Date().getFullYear()} Dynasty Metrics. Not affiliated with the NFL or Sleeper. All valuations are proprietary to Dynasty Metrics.
      </footer>
    </div>
  );
}
